import { motion } from "framer-motion";
import GeneralButton from "../../common/GeneralButton";
import { TiChevronLeft, TiPlus } from "react-icons/ti";
import { useStep, usePayment } from "../../contexts/SubscriptionContext";
import Icon from "../../common/Icon";
import CMSFormInput from "../../formFields/CMSFormInput";
import { Formik } from "formik";
import { cardSchema } from "../../utils/validations";
import { useToast } from "../../contexts/ToastContext";
import { useNavigate } from "react-router-dom";
import { FiArrowLeft, FiTrash } from "react-icons/fi";
import { useRef, useState, useEffect } from "react";
import { Card } from "react-bootstrap";
import Modal from "../../common/Modal";
import ContainerLoading from "../../common/loading/ContainerLoading";

const PaymentCards = () => {
  const { prevStep } = useStep();
  const {
    loading,
    savedCards,
    fetchCards,
    addCard,
    selectCard,
    updateCard,
    removeCard,
  } = usePayment();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [editingCard, setEditingCard] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const handleCloseModal = () => setSelectedId(null);
  const refs = {
    cardName: useRef(null),
    cardNumber: useRef(null),
    expiryMonth: useRef(null),
    expiryYear: useRef(null),
    cvv: useRef(null),
    isSaved: useRef(null),
  };
  useEffect(() => {
    fetchCards();
  }, []);
  useEffect(() => {
    if (!loading) {
      if (savedCards.length > 0) {
        setShowForm(false);
      } else {
        setShowForm(true);
      }
    }
  }, []);

  const scrollToFirstError = (errors) => {
    const firstErrorField = Object.keys(errors)[0];
    if (!firstErrorField || !refs[firstErrorField]?.current) return;

    const el = refs[firstErrorField].current;

    // Smoothly scroll the nearest scrollable parent
    el.scrollIntoView({
      behavior: "smooth",
      block: "center",
      inline: "nearest",
    });

    // Give the scroll some time to finish, then focus
    setTimeout(() => {
      el.focus?.({ preventScroll: true });
    }, 500); // adjust based on your animation speed
  };
  return (
    <motion.div
      initial={{ x: -200, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: -200, opacity: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className="mt-6 flex flex-col gap-6 bg-[#DEE2DD]/40 rounded-[15px] p-6 lg:px-12 w-full relative"
    >
      <div className="flex items-center gap-4 mb-0">
        {/* <button
          onClick={
            showForm && savedCards.length > 0
              ? () => {
                  setShowForm(false);
                  setEditingCard(null);
                }
              : () => navigate(-1)
          }
          className="flex items-center gap-2 pe-3 py-2 bg-gray-200 rounded hover:bg-gray-300 transition"
        >
          <FiArrowLeft size={30} />
        </button> */}
        <p className="text-24pt">
          {savedCards.length <= 0
            ? "Add Card"
            : editingCard
            ? "Edit Card"
            : "Saved Cards"}
        </p>
      </div>

      {/* Saved Cards */}
      {savedCards.length > 0 && !showForm && (
        <div className="flex flex-col gap-4">
          {savedCards.map((card) => {
            const maskedNumber = card.cardNumber
              ? "****" + "****" + "****" + card.cardNumber.slice(-4)
              : "";

            return (
              <div
                key={card._id}
                className={`grid w-full grid-cols-[2fr_4fr] lg:grid-cols-[1fr_1fr] bg-white rounded-[15px] px-8 py-4 cursor-pointer mb-4 
                  transition-transform duration-300 ease-in-out hover:scale-[1.02] hover:shadow-lg`}
                onClick={() => selectCard(card._id)}
              >
                <div className="flex flex-col justify-center ">
                  <p className="text-21pt"> {maskedNumber}</p>
                  <p className="text-18pt text-gray-600">
                    {card.cardHolderName}
                  </p>
                </div>

                <div className="flex items-center justify-end gap-4">
                  <Icon
                    icon="edit"
                    title="Click to edit card"
                    className="w-[27px] h-[32px] text-green hover:text-secondary cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();

                      setEditingCard(card);
                      setShowForm(true);
                    }}
                  />
                  <FiTrash
                    title="Click to remove card"
                    className="w-[27px] h-[32px] text-green hover:text-secondary cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedId(card._id);
                      // Remove card logic here
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
      {loading && (
        <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-20 rounded-lg">
          <ContainerLoading />
        </div>
      )}
      <Modal
        isOpen={selectedId != null}
        onClose={handleCloseModal}
        title="Remove Card"
        onConfirm={async () => {
          const success = await removeCard(selectedId);

          if (success) {
            handleCloseModal();
            addToast("Card removed successfully", "success");
          } else {
            setError("Failed to remove card. Please try again.");
            setTimeout(() => {
              setError("");
            }, 5000);
          }
        }}
      >
        <div className="font-mono">
          <p className="mt-2 font-medium">
            Are you sure you want to delete this card details?
          </p>
          {error && (
            <p
              className={`relative transition-all duration-300 ease-in-out w-full h-[20px]  flex items-center justify-center text-center mb-4 ${
                error ? "opacity-100 translate-y-3" : "opacity-0 -translate-y-2"
              }`}
            >
              {error && (
                <span className="errorMessage3 text-center">{error}</span>
              )}
            </p>
          )}
        </div>
      </Modal>
      {/* Show Form */}
      {showForm && (
        <Formik
          initialValues={{
            cardHolderName: editingCard?.cardHolderName || "",
            cardNumber: editingCard?.cardNumber || "",
            expiryMonth: editingCard?.expiryMonth || "",
            expiryYear: editingCard?.expiryYear || "",
            cvv: editingCard?.cvv || "",
            isSaved: "0",
          }}
          validationSchema={cardSchema}
          onSubmit={async (values, { resetForm }) => {
            if (editingCard) {
              const success = await updateCard(editingCard._id, values);

              if (success) {
                setEditingCard(null);
                addToast("Card updated successfully", "success");
                resetForm();
                setShowForm(false);
              } else {
                setError("Failed to update card. Please try again.");
                setTimeout(() => {
                  setError("");
                }, 5000);
              }
            } else {
              const newCard = {
                id: Date.now(),
                cardHolderName: values.cardHolderName,
                cardNumber: values.cardNumber,
                expiryMonth: values.expiryMonth,
                expiryYear: values.expiryYear,
                cardType: values.cardNumber?.startsWith("4")
                  ? "Visa"
                  : "Mastercard",
              };

              const success = await addCard(newCard);
              // console.log(success);

              if (success) {
                resetForm();
                setShowForm(false);
              } else {
                setError("Failed to add card");
                setTimeout(() => {
                  setError("");
                }, 5000);
              }
            }
          }}
        >
          {(form) => (
            <>
              {/* Existing form styling preserved */}

              <div className="grid grid-cols-1 gap-10 mt-4">
                <div className="rounded-[15px] p-0 flex flex-col justify-between">
                  <div className="w-full">
                    <div className="grid grid-cols-1 gap-8">
                      <CMSFormInput
                        name="cardHolderName"
                        label="Card Holder Name"
                        form={form}
                        className="1"
                        ref={refs.cardName}
                        required
                      />
                      <CMSFormInput
                        name="cardNumber"
                        label="Card Number"
                        className="1"
                        form={form}
                        ref={refs.cardNumber}
                        required
                      />
                    </div>
                    <div className="mt-8 flex flex-col md:flex-row gap-8 md:gap-0 md:items-center justify-between w-full max-w-[50%]">
                      <div className="flex items-end justify-start relative">
                        <CMSFormInput
                          name="expiryMonth"
                          label="End Date"
                          placeholder="mm"
                          className="max-w-[76px]"
                          form={form}
                          ref={refs.expiryMonth}
                          required
                          hideError={true}
                        />
                        <div className="text-21pt text-green w-[76px] mb-3">
                          /
                        </div>
                        <CMSFormInput
                          name="expiryYear"
                          label="111"
                          placeholder="yy"
                          className="max-w-[76px]"
                          textHidden="hidden"
                          form={form}
                          ref={refs.expiryYear}
                          required
                          hideError={true}
                        />
                        {form.errors.expiryMonth &&
                          form.touched.expiryMonth && (
                            <p className="absolute bottom-[-24px] errorMessage w-full">
                              {form.errors.expiryMonth ===
                                "Please enter a valid month" &&
                              form.errors.expiryYear
                                ? "Please enter a valid expiry month and year"
                                : form.errors.expiryMonth ||
                                  form.errors.expiryYear}
                            </p>
                          )}
                      </div>
                      <div className="flex items-end justify-start">
                        <CMSFormInput
                          name="cvv"
                          label="CVV"
                          type="password"
                          className="max-w-[125px] w-[125px]"
                          form={form}
                          ref={refs.cvv}
                          required
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {error && (
                <div
                  className={`relative transition-all duration-300 ease-in-out ${
                    error
                      ? "opacity-100 translate-y-0"
                      : "opacity-0 -translate-y-2"
                  }`}
                >
                  {error && <span className="errorMessage">{error}</span>}
                </div>
              )}
              <div className="grid grid-cols-1 lg:grid-cols-1 gap-10 mt-6">
                <div className="flex flex-col xs:flex-row gap-5 items-center justify-end">
                  {/* <GeneralButton
                    type="button"
                    text="Cancel"
                    bgColor="#fff"
                    textColor="#295B24"
                    iconPosition="Left"
                    icon={<TiChevronLeft size={24} />}
                    className="text-21pt px-4 brown"
                    onClick={() => {
                      setShowForm(false);
                      setEditingCard(null);
                    }}
                  /> */}
                  <GeneralButton
                    type="submit"
                    className="text-21pt px-4"
                    onClick={() => {
                      form.handleSubmit();
                      setTimeout(() => scrollToFirstError(form?.errors), 100);
                    }}
                    text={editingCard ? "Update Card" : "Add Card"}
                  />
                </div>
              </div>
            </>
          )}
        </Formik>
      )}

      {/* Add Card Button if no form visible */}
      {!showForm && (
        <div className="mt-0 flex justify-end">
          <GeneralButton
            onClick={() => setShowForm(true)}
            text="Add Card"
            textColor="#ccc"
            className="text-21pt px-4"
          />
        </div>
      )}
    </motion.div>
  );
};

export default PaymentCards;
